import { Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomButtonV2 from "../../../components/CustomButtonV2/CustomButtonV2.js";
import LoadingComponent from "../../../components/LoadingComponent/LoadingComponent.tsx";
import ModalConfirmacao from "../../../components/ModalConfirmacao/ModalConfirmacao.tsx";
import ModalLegenda from "../../../components/ModalLegenda/ModalLegenda.tsx";
import ModalUsuario from "../../../components/ModalUsuario/ModalUsuario.tsx";
import NavbarV3 from "../../../components/NavbarV3/NavbarV3.tsx";
import TabelaV2 from "../../../components/TabelaV2/TabelaV2.js";
import ToastAviso from "../../../components/Toast/ToastAviso/ToastAviso.tsx";
import ToastErro from "../../../components/Toast/ToastErro/ToastErro.tsx";
import ToastSucesso from "../../../components/Toast/ToastSucesso/ToastSucesso.tsx";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import { useTabela } from "../../../contexts/Tabela.js";
import Icon from "../../../core/components/Icon.tsx";
import MotionSemAutorizacao from "../../../core/components/MotionSemAutorizacao.tsx";
import Separator from "../../../core/components/Separator.tsx";
import { EmbarqueTemplate } from "../../../models/EmbarqueTemplate.ts";
import { getEmbarqueTemplateSearch } from "../../../services/ModuloGerenciador/EmbarqueTemplate.ts";
import {
	deleteTemplateEmbarquePorId,
	getTemplateEmbarque,
} from "../../../services/ModuloGerenciador/TemplateEmbarque.js";
import { IItemFiltro } from "./ModeloViagens.contants.ts";
import FiltroEmbarqueTemplate from "./components/FiltroEmbarqueTemplate.tsx";

function ModeloViagensHeader() {
  const { tabelaUsuario } = useProfile();
  const [openLegenda, setOpenLegenda] = useState(false);
  const [openConfigColuna, setOpenConfigColuna] = useState(false);

  const handleCloseLegenda = useCallback(() => {
    setOpenLegenda(false);
  }, []);

  const handleCloseConfig = useCallback(() => {
    setOpenConfigColuna(false);
  }, []);

  const mesAtual = useMemo(() => {
    const date = new Date();
    const mesPorExtenso = date.toLocaleString("pt-BR", { month: "long" });
    return mesPorExtenso;
  }, []);

  return (
    <div>
      <div className="header-modelo-viagens">
        <div className="header-modelo-viagens-left">
          <Separator direction="horizontal" size="md" />
        </div>
        <div className="header-modelo-viagens-right">
          <Tooltip title={mesAtual}>
            <Icon name="Info" size="lg" theme="dark" />
          </Tooltip>
        </div>
      </div>
      <ModalLegenda
        titulo="Legendas"
        open={openLegenda}
        onClose={handleCloseLegenda}
      />
    </div>
  );
}

function ModeloViagens() {
  const { profile, dadosFilial, hasRegra, tabelaUsuario } = useProfile();

  const { tabelaRedux, setTabelaRedux } = useTabela();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [valuesPesquisa, setValuesPesquisa] = useState<string>("");
  const [textoErro, setTextoErro] = useState<string>("");
  const [openToastErro, setOpenToastErro] = useState(false);
  const [textoSucesso, setTextoSucesso] = useState<string>("");
  const [openToastSucesso, setOpenToastSucesso] = useState(false);
  const [textoAviso, setTextoAviso] = useState<string>("");
  const [openToastAviso, setOpenToastAviso] = useState(false);
  const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
  const [openUsuario, setOpenUsuario] = useState(false);
  const [filtroSelecionado, setFiltroSelecionado] = useState<
    IItemFiltro | undefined
  >();
  const [semAutorizacao, setSemAutorizacao] = useState(false);
  const [dadosEmbarque, setDadosEmbarque] = useState<EmbarqueTemplate[] | null>(
    null
  );

  const buscarTemplatesInicial = useCallback(async () => {
    if (!profile) return;
    setLoading(true);
    await getTemplateEmbarque(profile?.token)
      .then((result) => {
        setDadosEmbarque(result.data);
        setTabelaRedux({});
      })
      .catch((error) => {
        if (typeof error.response.data === "string") {
          setTextoErro(error.response.data);
        } else {
          setTextoErro(
            "Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte."
          );
        }
        setOpenToastErro(true);
      });
    setLoading(false);
  }, [profile]);

  const buscarSearch = useCallback(async () => {
    if (!profile?.token) return;
    setLoading(true);
    await getEmbarqueTemplateSearch({
      token: profile.token,
      pesquisa: `${filtroSelecionado?.field}=${valuesPesquisa}`,
    })
      .then((result) => {
        setDadosEmbarque(result);
        setTabelaRedux({});
      })
      .catch((error) => {
        if (typeof error.response.data === "string") {
          setTextoErro(error.response.data);
        } else {
          setTextoErro(
            "Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte."
          );
        }
        setOpenToastErro(true);
      });
    setLoading(false);
  }, [profile, valuesPesquisa]);

  const deletarTemplatePorId = useCallback(async () => {
    if (!profile?.token) return;
    if (!tabelaRedux.colunaSelecionada) {
      setTextoErro("Selecione Uma Coluna");
      setOpenToastErro(true);
      return;
    }

    setLoading(true);
    await deleteTemplateEmbarquePorId(
      tabelaRedux.colunaSelecionada,
      profile?.token
    )
      .then((result) => {
        if (result.status === 200) {
          buscarTemplatesInicial();
          setTabelaRedux({});
          setOpenToastSucesso(true);
        }
      })
      .catch((error) => {
        if (typeof error.response.data === "string") {
          setTextoErro(error.response.data);
        } else {
          setTextoErro(
            "Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte."
          );
        }
        setOpenToastErro(true);
      });
    setLoading(false);
  }, [profile, tabelaRedux.colunaSelecionada, buscarTemplatesInicial]);

  const handleCloseToastErro = useCallback(() => {
    setOpenToastErro(false);
  }, []);

  const handleCloseToastSucesso = useCallback(() => {
    setOpenToastSucesso(false);
  }, []);

  const handleCloseToastAviso = useCallback(() => {
    setOpenToastAviso(false);
  }, []);

  const handleClearPesquisa = useCallback(() => {
    setValuesPesquisa("");
    setFiltroSelecionado(undefined);
    buscarTemplatesInicial();
  }, [buscarTemplatesInicial]);

  const handleClickDelete = useCallback(() => {
    if (tabelaRedux.colunaSelecionada) {
      setOpenModalConfirmacao(true);
      setTextoSucesso("Deletado Com Sucesso");
      return;
    }

    setTextoErro("Selecione Uma Coluna");
    setOpenToastErro(true);
  }, [tabelaRedux.colunaSelecionada]);

  const handleNavigateCadastro = useCallback(
    (props: any) => {
      navigate("/modelo-viagens/cadastro");
    },
    [navigate]
  );

  const handleNavigateEdicao = useCallback(
    (id: number) => {
      navigate(`/modelo-viagens/editar/${id}`);
    },
    [navigate]
  );

  const handleClickGetUser = useCallback(() => {
    if (tabelaRedux.usuarioAlteracao) {
      setOpenUsuario(true);
      return;
    }
    setTextoErro("Selecione Uma Coluna");
    setOpenToastErro(true);
  }, [tabelaRedux.usuarioAlteracao]);

  const handleCloseModalUser = useCallback(() => {
    setOpenUsuario(false);
  }, []);

  const handleCloseModalConfirmacao = useCallback(() => {
    setOpenModalConfirmacao(false);
  }, []);

  useEffect(() => {
    if (!profile) return;
    setSemAutorizacao(!hasRegra("Template Embarque"));
  }, [profile, dadosFilial, hasRegra]);

  useEffect(() => {
    if (profile) {
      buscarTemplatesInicial();
      setTabelaRedux({});
    }
  }, [profile, buscarTemplatesInicial]);

  const isLoading = useMemo(() => {
    return loading || !profile;
  }, [loading, profile]);

  if (semAutorizacao) {
    return <MotionSemAutorizacao />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <NavbarV3 title="Modelo Viagens" />
      {!isLoading && (
        <div>
          <div className="filtro-modelo-viagens">
            <FiltroEmbarqueTemplate
              buscarSearch={buscarSearch}
              setValuesPesquisa={setValuesPesquisa}
              valuesPesquisa={valuesPesquisa}
              filtroSelecionado={filtroSelecionado}
              setFiltroSelecionado={setFiltroSelecionado}
              handleClearPesquisa={handleClearPesquisa}
            />
            <div className="tabela-modelo-viagens">
              <TabelaV2
                setTelaCadastro={handleNavigateCadastro}
                setEditando={handleNavigateEdicao}
                classeSelecionado="idEmbarqueTemplete"
                nomeClasse1="outer-wrapper"
                nomeClasse2="table-wrapper-v2"
                colunas={tabelaUsuario?.ModeloEmbarque}
                dados={dadosEmbarque || []}
                nome="embarque"
                nomeTable="ModeloEmbarque"
              />
            </div>
          </div>
          <div className="container-botoes-emb">
            <div>
              <CustomButtonV2
                classCss="custom-button-cadastrar"
                classText="button-texto-cadastrar"
                icone="iconeSave"
                titulo="Cadastrar"
                cor="#0946B8"
                onClick={handleNavigateCadastro}
              />
              <CustomButtonV2
                classCss="custom-button-editar"
                classText="button-texto-editar"
                titulo="Editar"
                icone="iconeEdit"
                cor="#CDCDCD"
                onClick={() => {
                  if (tabelaRedux.colunaSelecionada) {
                    handleNavigateEdicao(tabelaRedux.colunaSelecionada);
                  } else {
                    setTextoErro("Selecione Uma Coluna");
                    setOpenToastErro(true);
                  }
                }}
              />
              <CustomButtonV2
                classCss="custom-button-deletar"
                classText="button-texto-cadastrar"
                titulo="Deletar"
                icone="iconeDelete"
                cor="#DC2D2D"
                onClick={handleClickDelete}
              />
            </div>
            <CustomButtonV2
              classCss="custom-button-user"
              classText="button-texto-user"
              titulo="Obter Usuário"
              cor="#FFFFFF"
              onClick={handleClickGetUser}
            />
          </div>
        </div>
      )}
      {isLoading && <LoadingComponent />}
      <ToastSucesso
        texto={textoSucesso}
        open={openToastSucesso}
        onClose={handleCloseToastSucesso}
      />
      <ToastErro
        texto={textoErro}
        open={openToastErro}
        onClose={handleCloseToastErro}
      />
      <ToastAviso
        texto={textoAviso}
        open={openToastAviso}
        onClose={handleCloseToastAviso}
      />
      <ModalConfirmacao
        titulo="Deletar"
        texto="Deseja Deletar?"
        open={openModalConfirmacao}
        onClose={handleCloseModalConfirmacao}
        onClick={deletarTemplatePorId}
      />
      <ModalUsuario
        titulo="Usuário"
        tituloConfirmar="OK"
        open={openUsuario}
        onClose={handleCloseModalUser}
      />
    </motion.div>
  );
}

export default memo(ModeloViagens);
